import { useEffect, useState, useCallback } from 'react';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import { fetchClubs, dbConfigured } from '../game/gameApi';
import ClubCrest from '../game/ClubCrest';
import ClubModal from '../components/ClubModal';
import { clubVars } from '../marketing/clubIdentity';

const TIER_NAMES = { 1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two' };
const TIERS = [1, 2, 3, 4];

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tx.muted, fontFamily: font.display, fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

export default function Clubs() {
  const [clubs, setClubs] = useState([]);
  const [status, setStatus] = useState('loading');
  const [tier, setTier] = useState(1);
  const [open, setOpen] = useState(null); // index into `visible`, or null

  useEffect(() => {
    let alive = true;
    if (!dbConfigured) { setStatus('error'); return undefined; }
    (async () => {
      const { data, error } = await fetchClubs();
      if (!alive) return;
      if (error) { setStatus('error'); return; }
      if (!data || data.length === 0) { setStatus('empty'); return; }
      setClubs(data);
      const first = data.reduce((m, c) => Math.min(m, c.tier), Infinity);
      setTier(Number.isFinite(first) ? first : 1);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, []);

  const visible = clubs.filter((c) => c.tier === tier);
  const len = visible.length;
  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i == null || !len ? i : (i - 1 + len) % len)), [len]);
  const next = useCallback(() => setOpen((i) => (i == null || !len ? i : (i + 1) % len)), [len]);

  const presentTiers = TIERS.filter((t) => clubs.some((c) => c.tier === t));

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: space.s6 }}>
      <div style={{ marginBottom: space.s5 }}>
        <div style={{
          fontFamily: font.display, fontSize: 11, fontWeight: 700,
          letterSpacing: '0.24em', textTransform: 'uppercase',
          color: accent.lime, marginBottom: space.s2,
        }}>
          The world
        </div>
        <h1 style={{ fontFamily: font.display, fontSize: 30, fontWeight: 700, margin: 0 }}>
          Explore the clubs.
        </h1>
        <p style={{ color: tx.secondary, marginTop: space.s3, maxWidth: '58ch' }}>
          Every club across the four divisions. Click any crest to read its history,
          rivalries and honours.
        </p>
      </div>

      {status === 'loading' && <Screen>Loading clubs</Screen>}
      {status === 'error' && <Screen>Couldn&rsquo;t reach the world database</Screen>}
      {status === 'empty' && <Screen>No clubs found</Screen>}

      {status === 'ready' && (
        <>
          {/* Division tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.s2, marginBottom: space.s5 }}>
            {presentTiers.map((t) => {
              const sel = t === tier;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setTier(t); setOpen(null); }}
                  style={{
                    cursor: 'pointer', padding: '8px 16px', borderRadius: 999,
                    border: `1px solid ${sel ? accent.lime : line.DEFAULT}`,
                    background: sel ? accent.lime : 'transparent',
                    color: sel ? bg.base : tx.secondary,
                    fontFamily: font.display, fontSize: 12.5, fontWeight: 700,
                    letterSpacing: '0.04em', transition: 'all .14s',
                  }}
                >
                  {TIER_NAMES[t] || `Tier ${t}`}
                </button>
              );
            })}
          </div>

          {/* Club grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: space.s3,
          }}>
            {visible.map((club, i) => {
              const cv = clubVars(club.primary_color);
              return (
                <button
                  key={club.id}
                  type="button"
                  onClick={() => setOpen(i)}
                  style={{
                    position: 'relative', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: space.s3,
                    background: bg.surface, border: `1px solid ${line.DEFAULT}`,
                    borderRadius: radius.lg, padding: space.s4, overflow: 'hidden',
                    transition: 'border-color .14s, background .14s, transform .14s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = cv.border;
                    e.currentTarget.style.background = bg.raised;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = line.DEFAULT;
                    e.currentTarget.style.background = bg.surface;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: cv.border }} />
                  <ClubCrest club={club} size={48} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{
                      display: 'block', fontFamily: font.display, fontWeight: 700, fontSize: 15,
                      color: tx.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {club.name}
                    </span>
                    {club.nickname && (
                      <span style={{
                        display: 'block', fontFamily: font.display, fontSize: 11, fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: tx.muted, marginTop: 3,
                      }}>
                        {club.nickname}
                      </span>
                    )}
                    <span style={{ display: 'block', fontFamily: font.data, fontSize: 11.5, color: tx.faint, marginTop: 3 }}>
                      {club.town}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {open != null && visible[open] && (
        <ClubModal club={visible[open]} onClose={close} onPrev={prev} onNext={next} />
      )}
    </div>
  );
}
